/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectTrendTargetDate,
  selectWeeklyTrendEndDate,
  selectWeeklyTrendStartDate,
} from 'modules/dashboard/selectors';
import {
  selectIsCumulative,
  selectTrendTimeWeeklyView,
  selectAdjustedStartDate,
  selectAdjustedEndDate,
  selectGranularity,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import {
  endOfMonth,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isSameDay,
  isSaturday,
  isSunday,
  lastDayOfYear,
  nextSaturday,
  previousSunday,
  startOfMonth,
  startOfYear,
} from 'date-fns';
import { trendActions } from 'modules/dashboard/components/tab-container/trend-tabs/slice';
import { getGranularity } from '../../trend-tabs/functions';
import { GRANULARITY } from '../../trend-tabs/constants';
/**
 * Custom hook to generate Trend Time widget filter schemas
 */
const useTrendTime = () => {
  const dispatch = useDispatch();
  //
  const targetDate = useSelector(selectTrendTargetDate);
  const weeklyViewToggle = useSelector(selectTrendTimeWeeklyView);
  const targetStartDate = useSelector(selectWeeklyTrendStartDate);
  const targetEndDate = useSelector(selectWeeklyTrendEndDate);
  const isCumulative = useSelector(selectIsCumulative);
  const adjustedStartDate = useSelector(selectAdjustedStartDate);
  const adjustedEndDate = useSelector(selectAdjustedEndDate);
  const granularity = useSelector(selectGranularity);
  //
  useEffect(() => {
    if (weeklyViewToggle) {
      dispatch(trendActions.setTrendStartDate(targetStartDate));
      dispatch(trendActions.setTrendEndDate(targetEndDate));
    } else if (granularity === GRANULARITY.DAILY) {
      dispatch(trendActions.setTrendStartDate(targetDate?.startDate));
      dispatch(trendActions.setTrendEndDate(targetDate?.endDate));
    } else {
      dispatch(trendActions.setTrendStartDate(adjustedStartDate));
      dispatch(trendActions.setTrendEndDate(adjustedEndDate));
    }
  }, [
    granularity,
    isCumulative,
    weeklyViewToggle,
    targetStartDate,
    targetEndDate,
    targetDate,
    adjustedEndDate,
    adjustedStartDate,
  ]);
  // Triggered when targetDate value changes
  useEffect(() => {
    // Set stay dates based on granularity
    const trendGranularity = getGranularity(targetDate);
    dispatch(trendActions.setGranularity(trendGranularity));
    if (trendGranularity === GRANULARITY.WEEKLY) {
      if (isSunday(new Date(targetDate.startDate))) {
        dispatch(trendActions.setAdjustedStartDate(new Date(targetDate.startDate).toISOString()));
      } else {
        dispatch(
          trendActions.setAdjustedStartDate(
            previousSunday(new Date(targetDate.startDate)).toISOString()
          )
        );
      }
      if (isSaturday(new Date(targetDate.endDate))) {
        dispatch(trendActions.setAdjustedEndDate(new Date(targetDate.endDate).toISOString()));
      } else {
        dispatch(
          trendActions.setAdjustedEndDate(nextSaturday(new Date(targetDate.endDate)).toISOString())
        );
      }
    } else if (trendGranularity === GRANULARITY.MONTHLY) {
      if (isFirstDayOfMonth(new Date(targetDate.startDate))) {
        dispatch(trendActions.setAdjustedStartDate(new Date(targetDate.startDate).toISOString()));
      } else {
        dispatch(
          trendActions.setAdjustedStartDate(
            startOfMonth(new Date(targetDate.startDate)).toISOString()
          )
        );
      }
      if (isLastDayOfMonth(new Date(targetDate.endDate))) {
        dispatch(trendActions.setAdjustedEndDate(new Date(targetDate.endDate).toISOString()));
      } else {
        dispatch(
          trendActions.setAdjustedEndDate(endOfMonth(new Date(targetDate.endDate)).toISOString())
        );
      }
    } else if (trendGranularity === GRANULARITY.YEARLY) {
      if (isSameDay(startOfYear(new Date(targetDate.startDate)), new Date(targetDate.startDate))) {
        dispatch(trendActions.setAdjustedStartDate(new Date(targetDate.startDate).toISOString()));
      } else {
        dispatch(
          trendActions.setAdjustedStartDate(
            startOfYear(new Date(targetDate.startDate)).toISOString()
          )
        );
      }
      if (isSameDay(lastDayOfYear(new Date(targetDate.endDate)), new Date(targetDate.endDate))) {
        dispatch(trendActions.setAdjustedEndDate(new Date(targetDate.endDate).toISOString()));
      } else {
        dispatch(
          trendActions.setAdjustedEndDate(lastDayOfYear(new Date(targetDate.endDate)).toISOString())
        );
      }
    }
  }, [targetDate]);
};
//
export default useTrendTime;
